// Conversation API service for managing chat sessions

import type { Conversation, BackendMessage } from '../types/chat';
import { authService } from './authService';

const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
  conversationsEndpoint: '/api/conversations'
};

export class ConversationApiService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_CONFIG.baseUrl;
  }

  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = authService.getToken();
    if (token && authService.isAuthenticated()) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        authService.removeToken();
        window.location.href = '/';
        return Promise.reject(new Error('Authentication required'));
      }

      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If JSON parsing fails, use the default HTTP error message
      }

      throw new Error(errorMessage);
    }

    try {
      return await response.json();
    } catch {
      // If response is not JSON, return empty array for GET requests
      if (response.status === 200) {
        return [] as unknown as T;
      }
      throw new Error('Invalid response format');
    }
  }

  /**
   * Get all conversations for the current user
   */
  async getConversations(): Promise<Conversation[]> {
    const response = await fetch(`${this.baseUrl}${API_CONFIG.conversationsEndpoint}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<Conversation[]>(response);
  }

  /**
   * Get recent conversations for the current user
   */
  async getRecentConversations(limit: number = 10): Promise<Conversation[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}${API_CONFIG.conversationsEndpoint}/recent?limit=${limit}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      return this.handleResponse<Conversation[]>(response);
    } catch (error) {
      // Handle network errors or other fetch failures
      if (error instanceof Error && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server');
      }
      throw error;
    }
  }

  /**
   * Get a specific conversation by ID
   */
  async getConversation(conversationId: string): Promise<Conversation> {
    const response = await fetch(
      `${this.baseUrl}${API_CONFIG.conversationsEndpoint}/${conversationId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    return this.handleResponse<Conversation>(response);
  }

  /**
   * Create a new conversation
   */
  async createConversation(conversationId?: string): Promise<Conversation> {
    const body = conversationId ? { conversationId } : {};

    const response = await fetch(`${this.baseUrl}${API_CONFIG.conversationsEndpoint}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    });

    return this.handleResponse<Conversation>(response);
  }

  /**
   * Update conversation title
   */
  async updateConversationTitle(conversationId: string, title: string): Promise<Conversation> {
    const response = await fetch(
      `${this.baseUrl}${API_CONFIG.conversationsEndpoint}/${conversationId}/title`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ title }),
      }
    );

    return this.handleResponse<Conversation>(response);
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}${API_CONFIG.conversationsEndpoint}/${conversationId}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        authService.removeToken();
        window.location.href = '/';
        return Promise.reject(new Error('Authentication required'));
      }
      throw new Error(`Failed to delete conversation: ${response.statusText}`);
    }
  }

  /**
   * Get conversation messages/history
   */
  async getConversationMessages(conversationId: string): Promise<BackendMessage[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}${API_CONFIG.conversationsEndpoint}/${conversationId}/messages`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      return this.handleResponse<BackendMessage[]>(response);
    } catch (error) {
      console.error('Failed to load conversation messages:', error);

      // Re-throw authentication errors instead of returning empty array
      if (error instanceof Error && error.message.includes('Authentication required')) {
        throw error;
      }

      // For other errors, return empty array to allow fallback behavior
      return [];
    }
  }
}

export const conversationApi = new ConversationApiService();